import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery } from '@tanstack/react-query';
import { OwnCredentialSchema, RecoveryCodesResponseSchema } from '@taqon/contracts';
import { getJson } from '../../shared/api/client';
import { FormDialog } from '../../shared/ui/forms';
import { postAuth } from './auth-api';
import { CodeField, PasswordField, RecoveryCodes } from './AuthCard';

/** Whether the signed-in user signs in with the built-in password (SDD §11.1.2). */
export function useOwnCredential() {
  return useQuery({
    queryKey: ['me', 'credential'],
    retry: false,
    queryFn: () => getJson('/api/v1/me/credential', OwnCredentialSchema),
  });
}

/**
 * FRD-USR-026: the current password and, where the second factor is required, an authenticator
 * code confirm a new password.
 */
export function ChangePasswordDialog({
  csrfToken,
  secondFactor,
  onClose,
}: {
  csrfToken: string;
  secondFactor: boolean;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [code, setCode] = useState('');
  const [done, setDone] = useState(false);
  const change = useMutation({
    mutationFn: () =>
      postAuth(
        '/api/v1/me/password',
        { currentPassword: current, newPassword: next, ...(secondFactor ? { code } : {}) },
        null,
        csrfToken,
      ),
    onSuccess: () => {
      setDone(true);
    },
    onError: () => {
      setCode('');
    },
  });
  if (done) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Password changed</DialogTitle>
        <DialogContent>
          <Typography>
            Your password is changed. Your other signed-in sessions have been signed out.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  const mismatch = repeat !== '' && repeat !== next;
  return (
    <FormDialog
      open
      title="Change password"
      submitLabel="Change password"
      canSubmit={
        current !== '' && next !== '' && next === repeat && (!secondFactor || code.length === 6)
      }
      submitting={change.isPending}
      error={change.isError ? change.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        change.mutate();
      }}
    >
      <PasswordField
        label="Current password"
        autoComplete="current-password"
        required
        value={current}
        onChange={(e) => {
          setCurrent(e.target.value);
        }}
      />
      <PasswordField
        label="New password"
        autoComplete="new-password"
        required
        value={next}
        onChange={(e) => {
          setNext(e.target.value);
        }}
      />
      <PasswordField
        label="Repeat the new password"
        autoComplete="new-password"
        required
        value={repeat}
        error={mismatch}
        helperText={mismatch ? 'The passwords are not the same.' : ' '}
        onChange={(e) => {
          setRepeat(e.target.value);
        }}
      />
      {secondFactor && <CodeField value={code} onCode={setCode} />}
    </FormDialog>
  );
}

/** FRD-USR-027: replaces every unused recovery code; the new ones are shown once. */
export function RecoveryCodesDialog({
  csrfToken,
  remaining,
  onClose,
}: {
  csrfToken: string;
  remaining: number;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');
  const replace = useMutation({
    mutationFn: () =>
      postAuth('/api/v1/me/recovery-codes', { code }, RecoveryCodesResponseSchema, csrfToken),
    onError: () => {
      setCode('');
    },
  });
  if (replace.data !== undefined) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>New recovery codes</DialogTitle>
        <DialogContent>
          <RecoveryCodes codes={replace.data.recoveryCodes} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>I have saved them</Button>
        </DialogActions>
      </Dialog>
    );
  }
  return (
    <FormDialog
      open
      title="New recovery codes"
      submitLabel="Create new codes"
      canSubmit={code.length === 6}
      submitting={replace.isPending}
      error={replace.isError ? replace.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        replace.mutate();
      }}
    >
      <Typography variant="body2">
        You have {remaining} unused recovery {remaining === 1 ? 'code' : 'codes'}. New codes replace
        all of them. Confirm with the code from your authenticator app.
      </Typography>
      <CodeField value={code} onCode={setCode} />
    </FormDialog>
  );
}
